import * as THREE from 'three';

// A GPU-disabled Chrome does not throw a helpful error: THREE.WebGLRenderer's
// constructor logs the browser's reason to the console and rethrows a bare
// "Error creating WebGL context.", so the reason has to be caught off the
// canvas's own webglcontextcreationerror event, which fires synchronously
// during construction, before three's own listener for the same event.
function paintNotice(mount: HTMLElement, reason: string): void {
  const notice = document.createElement('div');
  notice.id = 'webgl-notice';
  notice.className = 'panel';
  // Carries its own colours: artifact-viewer.html defines none of the HUD
  // classes below, so a class-only notice paints black on its near-black body.
  notice.style.cssText =
    'position:absolute;inset:0;z-index:1000;display:flex;flex-direction:column;' +
    'align-items:center;justify-content:center;gap:14px;text-align:center;padding:48px;' +
    'background:rgba(4,9,17,0.95);color:#cfe9ff;' +
    'font-family:"SF Mono",Menlo,Consolas,monospace;font-size:12px;line-height:1.6;';

  const title = document.createElement('div');
  title.className = 'panel-title';
  title.style.cssText = 'font-size:15px;letter-spacing:0.18em;color:#9fdcff;';
  title.textContent = 'The scene could not start';

  const summary = document.createElement('div');
  summary.className = 'val';
  summary.style.color = '#cfe9ff';
  summary.textContent = 'WebGL is unavailable in this browser right now.';

  const reasonBlock = document.createElement('pre');
  reasonBlock.id = 'webgl-notice-reason';
  reasonBlock.className = 'dim';
  reasonBlock.style.cssText =
    'max-width:640px;white-space:pre-wrap;font-size:10px;margin:0;font-family:inherit;color:#7fa8c0;';
  reasonBlock.textContent = reason || '(the browser gave no reason)';

  const line1 = document.createElement('div');
  line1.className = 'dim';
  line1.style.color = '#7fa8c0';
  line1.textContent =
    'Quit the browser completely and reopen it; Chrome keeps the GPU switched off ' +
    'for the rest of a session after its GPU process has crashed.';

  const line2 = document.createElement('div');
  line2.className = 'dim';
  line2.style.color = '#7fa8c0';
  line2.textContent = 'Then open chrome://gpu and check that WebGL reads Hardware accelerated.';

  const reload = document.createElement('button');
  reload.id = 'webgl-notice-reload';
  reload.className = 'panel-btn';
  reload.style.cssText =
    'font:inherit;color:#cfe9ff;background:rgba(20,44,66,0.9);cursor:pointer;' +
    'border:1px solid rgba(120,220,255,0.42);border-radius:3px;padding:6px 18px;';
  reload.textContent = 'Reload';
  reload.addEventListener('click', () => location.reload());

  notice.append(title, summary, reasonBlock, line1, line2, reload);
  mount.appendChild(notice);
}

/**
 * Try to construct a WebGLRenderer. On failure, paint a full-screen notice
 * into `mount` (in the app's existing HUD styling) and return null instead of
 * letting the constructor's throw kill the rest of the module.
 */
export function createRendererOrExplain(
  params: THREE.WebGLRendererParameters,
  mount: HTMLElement,
): THREE.WebGLRenderer | null {
  const canvas = document.createElement('canvas');
  let reason = '';
  const captureReason = (event: Event): void => {
    reason = (event as WebGLContextEvent).statusMessage || reason;
  };
  canvas.addEventListener('webglcontextcreationerror', captureReason, false);
  try {
    return new THREE.WebGLRenderer({ ...params, canvas });
  } catch (err) {
    if (!reason) {
      // Fallback for a throw that carried no creation-error event: ask the
      // scratch canvas directly, which re-triggers the same event.
      canvas.getContext('webgl2');
    }
    const message = reason || (err instanceof Error ? err.message : String(err));
    paintNotice(mount, message);
    return null;
  } finally {
    canvas.removeEventListener('webglcontextcreationerror', captureReason, false);
  }
}

/**
 * Listen for the canvas losing and regaining its GL context. Returns a
 * dispose function that removes both listeners.
 */
export function watchContext(
  renderer: THREE.WebGLRenderer,
  onLost: () => void,
  onRestored: () => void,
): () => void {
  const canvas = renderer.domElement;
  const handleLost = (event: Event): void => {
    event.preventDefault(); // tells the browser it may attempt to restore the context
    onLost();
  };
  const handleRestored = (): void => onRestored();
  canvas.addEventListener('webglcontextlost', handleLost, false);
  canvas.addEventListener('webglcontextrestored', handleRestored, false);
  return () => {
    canvas.removeEventListener('webglcontextlost', handleLost, false);
    canvas.removeEventListener('webglcontextrestored', handleRestored, false);
  };
}
